import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ProjectType } from '../../models/sow';
import { ChefHat, Bath, Home, Building, Wrench, ArrowRight, CheckCircle } from 'lucide-react';

interface ScopeGatheringStepProps {
  onProjectTypeSelected: (projectType: ProjectType) => void;
  address: string;
}

const projectTypeOptions = [
  { value: 'Kitchen' as ProjectType, icon: ChefHat, description: 'Kitchen remodel or renovation' },
  { value: 'Bathroom' as ProjectType, icon: Bath, description: 'Bathroom renovation or updates' },
  { value: 'Full House' as ProjectType, icon: Home, description: 'Whole home renovation' },
  { value: 'Basement' as ProjectType, icon: Building, description: 'Basement finishing or remodel' },
  { value: 'Custom' as ProjectType, icon: Wrench, description: 'Other renovation type' }
];

export const ScopeGatheringStep: React.FC<ScopeGatheringStepProps> = ({
  onProjectTypeSelected,
  address,
}) => {
  const [selectedProjectType, setSelectedProjectType] = useState<ProjectType | ''>('');

  const handleContinue = () => {
    if (selectedProjectType) {
      onProjectTypeSelected(selectedProjectType);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Define Your Project Scope</h1>
        <p className="text-gray-600">
          Now let's understand what you'd like to renovate at <strong>{address}</strong>
        </p>
      </div>

      <Card className="border-0 shadow-none">
        <CardHeader>
          <CardTitle>What type of renovation are you planning?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3">
            {projectTypeOptions.map((option) => {
              const IconComponent = option.icon;
              const isSelected = selectedProjectType === option.value;
              
              return (
                <div
                  key={option.value}
                  onClick={() => setSelectedProjectType(option.value)}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <IconComponent className={`h-6 w-6 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-medium">{option.value}</h3>
                      <p className="text-sm text-gray-600">{option.description}</p>
                    </div>
                    {isSelected && (
                      <div className="flex-shrink-0">
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {selectedProjectType && (
            <div className="grid grid-cols-2 gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setSelectedProjectType('')}>
                Clear
              </Button>
              <Button onClick={handleContinue}>
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};